import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Save, Upload, Trash2, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { RichTextEditor } from "@/components/admin/RichTextEditor";

export const Route = createFileRoute("/_authenticated/admin/locatie")({
  head: () => ({
    meta: [
      { title: "Admin — Locatie beheren" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminLocationPage,
});

type GalleryItem = { path: string; caption?: string };

type Row = {
  title: string;
  intro: string;
  body: string;
  map_path: string | null;
  gallery: GalleryItem[];
};

function useAdmin() {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return setIsAdmin(false);
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", u.user.id)
        .eq("role", "admin");
      setIsAdmin(!!roles && roles.length > 0);
    })();
  }, []);
  return isAdmin;
}

async function sign(path: string | null | undefined): Promise<string | null> {
  if (!path) return null;
  const { data } = await supabase.storage.from("location").createSignedUrl(path, 60 * 60);
  return data?.signedUrl ?? null;
}

function AdminLocationPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isAdmin = useAdmin();
  const [row, setRow] = useState<Row | null>(null);
  const [saving, setSaving] = useState(false);
  const [mapPreview, setMapPreview] = useState<string | null>(null);
  const [galleryPreviews, setGalleryPreviews] = useState<Record<string, string>>({});

  async function load() {
    const { data, error } = await supabase
      .from("location_page")
      .select("title, intro, body, map_path, gallery")
      .eq("id", "default")
      .maybeSingle();
    if (error) return toast.error(error.message);
    const record: Row = {
      title: data?.title ?? "Stadspark Berg en Bos",
      intro: data?.intro ?? "",
      body: data?.body ?? "",
      map_path: (data?.map_path as string | null) ?? null,
      gallery: ((data?.gallery as GalleryItem[] | null) ?? []),
    };
    setRow(record);
    setMapPreview(await sign(record.map_path));
    const previews: Record<string, string> = {};
    await Promise.all(
      record.gallery.map(async (g) => {
        const u = await sign(g.path);
        if (u) previews[g.path] = u;
      }),
    );
    setGalleryPreviews(previews);
  }

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  }

  async function uploadTo(kind: "map" | "gallery", file: File) {
    if (!row) return;
    const ext = file.name.split(".").pop() || "jpg";
    const path = `${kind}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const up = await supabase.storage.from("location").upload(path, file, {
      contentType: file.type,
      upsert: false,
    });
    if (up.error) return toast.error("Upload mislukt: " + up.error.message);

    if (kind === "map") {
      const { error } = await supabase
        .from("location_page")
        .update({ map_path: path })
        .eq("id", "default");
      if (error) return toast.error(error.message);
    } else {
      const nextGallery = [...row.gallery, { path, caption: "" }];
      const { error } = await supabase
        .from("location_page")
        .update({ gallery: nextGallery })
        .eq("id", "default");
      if (error) return toast.error(error.message);
    }
    toast.success("Geüpload");
    await load();
    qc.invalidateQueries({ queryKey: ["location-page"] });
  }

  async function removeMap() {
    const { error } = await supabase
      .from("location_page")
      .update({ map_path: null })
      .eq("id", "default");
    if (error) return toast.error(error.message);
    toast.success("Plattegrond verwijderd");
    await load();
    qc.invalidateQueries({ queryKey: ["location-page"] });
  }

  async function persistGallery(next: GalleryItem[]) {
    const { error } = await supabase
      .from("location_page")
      .update({ gallery: next })
      .eq("id", "default");
    if (error) return toast.error(error.message);
    setRow((r) => (r ? { ...r, gallery: next } : r));
    qc.invalidateQueries({ queryKey: ["location-page"] });
  }

  async function removeGalleryItem(idx: number) {
    if (!row) return;
    const next = row.gallery.filter((_, i) => i !== idx);
    await persistGallery(next);
  }

  async function moveGalleryItem(idx: number, dir: -1 | 1) {
    if (!row) return;
    const next = [...row.gallery];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    await persistGallery(next);
  }

  function setCaption(idx: number, caption: string) {
    setRow((r) =>
      r ? { ...r, gallery: r.gallery.map((g, i) => (i === idx ? { ...g, caption } : g)) } : r,
    );
  }

  async function saveText() {
    if (!row) return;
    setSaving(true);
    const { error } = await supabase
      .from("location_page")
      .update({
        title: row.title,
        intro: row.intro,
        body: row.body,
        gallery: row.gallery,
      })
      .eq("id", "default");
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Opgeslagen");
    qc.invalidateQueries({ queryKey: ["location-page"] });
  }

  if (isAdmin === null || row === null) {
    return <div className="flex min-h-screen items-center justify-center text-foreground/70">Laden…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-5">
        <div className="max-w-md space-y-4 text-center">
          <h1 className="font-display text-2xl">Geen toegang</h1>
          <p className="text-foreground/70">Je account heeft geen admin rechten.</p>
          <Button onClick={handleSignOut} variant="ghostLine">Uitloggen</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-5 py-12 md:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <p className="font-mono-cinema text-[10px] text-gold">— Admin</p>
          <h1 className="mt-2 font-display text-3xl font-medium md:text-4xl">Locatie beheren</h1>
          <p className="mt-2 text-sm text-foreground/70">Tekst, plattegrond en foto's van de pagina Stadspark Berg en Bos.</p>
        </div>

        <section className="rounded-2xl border border-border bg-night-deep/50 p-6 md:p-8">
          <h2 className="font-display text-xl">Tekst</h2>
          <div className="mt-5 space-y-4">
            <div>
              <Label>Titel</Label>
              <Input
                value={row.title}
                onChange={(e) => setRow({ ...row, title: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Intro (korte lead-tekst)</Label>
              <div className="mt-1">
                <RichTextEditor
                  value={row.intro}
                  onChange={(html) => setRow({ ...row, intro: html })}
                  minHeight={110}
                />
              </div>
            </div>
            <div>
              <Label>Hoofdtekst</Label>
              <div className="mt-1">
                <RichTextEditor
                  value={row.body}
                  onChange={(html) => setRow({ ...row, body: html })}
                  minHeight={320}
                />
              </div>
              <p className="mt-1 text-xs text-foreground/60">Gebruik de werkbalk voor koppen, opsommingen, vet en links.</p>
            </div>
            <Button variant="hero" size="sm" onClick={saveText} disabled={saving}>
              <Save className="h-4 w-4" /> {saving ? "Bezig…" : "Tekst opslaan"}
            </Button>
          </div>
        </section>

        <section className="mt-8 rounded-2xl border border-border bg-night-deep/50 p-6 md:p-8">
          <h2 className="font-display text-xl">Plattegrond</h2>
          <p className="mt-1 text-sm text-foreground/70">Upload een afbeelding van de plattegrond (JPG/PNG/WebP).</p>
          <div className="mt-4 flex flex-col items-start gap-4 md:flex-row">
            <div className="flex h-48 w-full max-w-md items-center justify-center overflow-hidden rounded-lg border border-border bg-night">
              {mapPreview ? (
                <img src={mapPreview} alt="Plattegrond" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="text-xs text-foreground/50">Nog geen plattegrond</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-secondary">
                <Upload className="h-4 w-4" />
                <span>{row.map_path ? "Vervangen" : "Uploaden"}</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadTo("map", f);
                    e.currentTarget.value = "";
                  }}
                />
              </label>
              {row.map_path && (
                <Button variant="ghostLine" size="sm" onClick={removeMap}>
                  <Trash2 className="h-4 w-4" /> Verwijderen
                </Button>
              )}
            </div>
          </div>
        </section>

        <section className="mt-8 rounded-2xl border border-border bg-night-deep/50 p-6 md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-xl">Foto's</h2>
              <p className="mt-1 text-sm text-foreground/70">Sfeerfoto's van het festivalterrein.</p>
            </div>
            <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-secondary">
              <Plus className="h-4 w-4" />
              <span>Foto toevoegen</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadTo("gallery", f);
                  e.currentTarget.value = "";
                }}
              />
            </label>
          </div>

          {row.gallery.length === 0 ? (
            <p className="mt-6 text-sm text-foreground/60">Nog geen foto's toegevoegd.</p>
          ) : (
            <div className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
              {row.gallery.map((g, i) => (
                <div key={g.path} className="overflow-hidden rounded-xl border border-border bg-night/40">
                  <div className="aspect-[4/3] w-full overflow-hidden bg-night">
                    {galleryPreviews[g.path] ? (
                      <img
                        src={galleryPreviews[g.path]}
                        alt={g.caption ?? "Sfeerfoto"}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs text-foreground/50">Laden…</div>
                    )}
                  </div>
                  <div className="space-y-2 p-3">
                    <Input
                      value={g.caption ?? ""}
                      placeholder="Bijschrift (optioneel)"
                      onChange={(e) => setCaption(i, e.target.value)}
                      onBlur={() => row && persistGallery(row.gallery)}
                      className="text-sm"
                    />
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1">
                        <Button variant="ghostLine" size="sm" onClick={() => moveGalleryItem(i, -1)} disabled={i === 0}>
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button variant="ghostLine" size="sm" onClick={() => moveGalleryItem(i, 1)} disabled={i === row.gallery.length - 1}>
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                      <Button variant="ghostLine" size="sm" onClick={() => removeGalleryItem(i)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      <Toaster position="top-center" />
    </div>
  );
}